import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { format, parse, isSameDay, isValid } from 'date-fns';
import DayPicker, { DayModifiers } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import LoaderComponent from '../../Shared/Loader';
import { AuthContext } from '../../Providers/UserProvider';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { firebase } from '../../firebase';

/**
 * True when `date` is on a calendar day before `now`.
 * A vacation day equal to today stays active for the rest of that local day.
 */
export function isDateBeforeToday(date: Date, now: Date = new Date()): boolean {
    return format(date, 'yyyy-MM-dd') < format(now, 'yyyy-MM-dd');
}

function writeAlertMessage(error: unknown, fallback: string): string {
    const detail = rejectionMessage(error, '');
    return detail ? `${fallback} ${detail}` : fallback;
}

function parseVacationDays(vacationDays: Array<unknown>): Date[] {
    return vacationDays.flatMap((vacationDay) => {
        if (typeof vacationDay !== 'string') return [];
        const parsed = parse(vacationDay, 'yyyy-MM-dd', new Date());
        return isValid(parsed) ? [parsed] : [];
    });
}

const VacationPage = () => {
    const { techId, techsRef } = useContext(AuthContext);
    const [selectedVacationDays, setSelectedVacationDays] = useState<Array<Date>>([]);
    const vacationDaysRef = useRef<Date[]>([]);
    const daysLoadedRef = useRef(false);
    const loadGenerationRef = useRef(0);
    const writeTailRef = useRef<Promise<void>>(Promise.resolve());
    const writeQueueRef = useRef<string[]>([]);
    const [daysLoaded, setDaysLoaded] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [writeError, setWriteError] = useState('');
    const [reloadNonce, setReloadNonce] = useState(0);
    const [today, setToday] = useState(() => format(new Date(), 'yyyy-MM-dd'));

    const markDaysLoaded = (loaded: boolean) => {
        daysLoadedRef.current = loaded;
        setDaysLoaded(loaded);
    };

    const enqueueVacationChange = (day: Date, adding: boolean) => {
        const dayKey = format(day, 'yyyy-MM-dd');
        const generation = loadGenerationRef.current;
        const docRef = techsRef.doc(techId);
        const vacation = adding
            ? firebase.firestore.FieldValue.arrayUnion(dayKey)
            : firebase.firestore.FieldValue.arrayRemove(dayKey);

        // One update at a time. Each update adds or removes only this date.
        writeQueueRef.current.push(dayKey);
        writeTailRef.current = writeTailRef.current
            .catch(() => undefined)
            .then(async () => {
                writeQueueRef.current.shift();
                if (generation !== loadGenerationRef.current || !daysLoadedRef.current) return;
                try {
                    await docRef.update({ vacation });
                } catch (error) {
                    if (generation !== loadGenerationRef.current) return;
                    console.error('Vacation days could not be updated.', error);
                    const laterClickOnSameDay = writeQueueRef.current.includes(dayKey);
                    if (!laterClickOnSameDay) {
                        const current = vacationDaysRef.current;
                        const hasDay = current.some((dateItem) => isSameDay(dateItem, day));
                        const reverted = adding
                            ? current.filter((dateItem) => !isSameDay(dateItem, day))
                            : hasDay
                            ? current
                            : current.concat(day);
                        vacationDaysRef.current = reverted;
                        setSelectedVacationDays([...reverted]);
                    }
                    setWriteError(writeAlertMessage(error, 'Vacation days could not be updated.'));
                }
            });
    };

    useEffect(() => {
        let cancelled = false;
        const generation = ++loadGenerationRef.current;
        markDaysLoaded(false);
        vacationDaysRef.current = [];
        setSelectedVacationDays([]);
        setShowLoader(true);
        setLoadError('');
        techsRef
            .doc(techId)
            .get()
            .then((snapshot) => {
                if (cancelled || generation !== loadGenerationRef.current) return;
                const data = (snapshot.data() ?? {}) as Partial<{ vacation?: Array<unknown> }>;
                const nextDays = parseVacationDays(data.vacation ?? []);
                vacationDaysRef.current = nextDays;
                setSelectedVacationDays(nextDays);
                markDaysLoaded(true);
                setShowLoader(false);
            })
            .catch((error) => {
                if (cancelled || generation !== loadGenerationRef.current) return;
                console.error('Vacation days could not be loaded.', error);
                markDaysLoaded(false);
                setLoadError(writeAlertMessage(error, 'Vacation days could not be loaded.'));
                setShowLoader(false);
            });
        return () => {
            cancelled = true;
        };
    }, [techId, techsRef, reloadNonce]);

    useEffect(() => {
        const onOnline = () => {
            if (daysLoadedRef.current) return;
            setReloadNonce((nonce) => nonce + 1);
        };
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
    }, []);

    useEffect(() => {
        let midnightTimer: ReturnType<typeof setTimeout> | undefined;

        const syncToday = () => {
            const next = format(new Date(), 'yyyy-MM-dd');
            setToday((current) => (current === next ? current : next));
        };

        const scheduleMidnight = () => {
            if (midnightTimer !== undefined) clearTimeout(midnightTimer);
            const now = new Date();
            const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
            midnightTimer = setTimeout(() => {
                syncToday();
                scheduleMidnight();
            }, nextMidnight.getTime() - now.getTime());
        };

        let cancelled = false;
        let refreshSerial = 0;

        // Replace the visible days from the server when this tab returns.
        // Skip while a write is queued, and do not blank the calendar or show
        // the loader — that reset belongs to the reloadNonce load.
        const refreshVacationDays = () => {
            if (writeQueueRef.current.length > 0 || !daysLoadedRef.current) return;
            const generation = loadGenerationRef.current;
            const requestId = ++refreshSerial;
            const writeTail = writeTailRef.current;
            techsRef
                .doc(techId)
                .get()
                .then((snapshot) => {
                    if (
                        cancelled ||
                        requestId !== refreshSerial ||
                        generation !== loadGenerationRef.current ||
                        writeTailRef.current !== writeTail ||
                        writeQueueRef.current.length > 0 ||
                        !daysLoadedRef.current
                    ) {
                        return;
                    }
                    const data = (snapshot.data() ?? {}) as Partial<{ vacation?: Array<unknown> }>;
                    const nextDays = parseVacationDays(data.vacation ?? []);
                    vacationDaysRef.current = nextDays;
                    setSelectedVacationDays(nextDays);
                    setLoadError('');
                })
                .catch((error) => {
                    if (cancelled || requestId !== refreshSerial || generation !== loadGenerationRef.current) return;
                    console.error('Vacation days could not be loaded.', error);
                    setLoadError(writeAlertMessage(error, 'Vacation days could not be loaded.'));
                });
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            syncToday();
            scheduleMidnight();
            refreshVacationDays();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        scheduleMidnight();

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (midnightTimer !== undefined) clearTimeout(midnightTimer);
        };
    }, [techId, techsRef]);

    // Active days are selected vacation days on today's calendar date or later.
    const todayDate = useMemo(() => parse(today, 'yyyy-MM-dd', new Date()), [today]);
    const activeDays: number = useMemo(
        () => selectedVacationDays.filter((vacationDate) => !isDateBeforeToday(vacationDate, todayDate)).length,
        [selectedVacationDays, todayDate],
    );

    const handleDayClick = (day: Date, modifiers: DayModifiers) => {
        if (!daysLoadedRef.current || modifiers.disabled || isDateBeforeToday(day, todayDate)) return;

        setWriteError('');
        const previousDays = vacationDaysRef.current;
        const alreadySelected = previousDays.some((dateItem) => isSameDay(dateItem, day));
        const updatedSelectedDays = alreadySelected
            ? previousDays.filter((dateItem) => !isSameDay(dateItem, day))
            : previousDays.concat(day);
        vacationDaysRef.current = updatedSelectedDays;
        setSelectedVacationDays([...updatedSelectedDays]);
        enqueueVacationChange(day, !alreadySelected);
    };

    return (
        <div>
            <LoaderComponent showLoader={showLoader} />
            <div className="bg-white-600 py-4 text-center shadow-lg">
                <div className="my-auto pl-2.5 pr-2.5">
                    <p className="text-2xl text-gray-800 font-semibold">Vacation Days</p>
                    <p className="text-lg text-gray-800">{activeDays} active vacation days</p>
                    {loadError ? (
                        <p className="text-red-600 text-sm mt-4" role="alert" data-testid="vacation-load-error">
                            {loadError}
                        </p>
                    ) : null}
                    {writeError ? (
                        <p className="text-red-600 text-sm mt-4" role="alert" data-testid="vacation-write-error">
                            {writeError}
                        </p>
                    ) : null}
                </div>
            </div>
            <div className="p-8">
                <DayPicker
                    disabledDays={daysLoaded ? { before: todayDate } : () => true}
                    className="w-full shadow-lg lg:shadow-none"
                    selectedDays={selectedVacationDays}
                    onDayClick={handleDayClick}
                />
            </div>
        </div>
    );
};
export default VacationPage;
